/**
 * Shared custom validators for @vuelidate/validators.
 *
 * All application forms should import validators from this file
 * instead of defining them locally.
 */
import {
  helpers,
} from '@vuelidate/validators';
import { FinnishSSN } from 'finnish-ssn';
import translate from './translate';
import { sprintf } from 'sprintf-js';

/**
 * Helper: checks if a value is considered "filled" (not empty/null/false).
 */
export const filled = (value) => !['', null, false].includes(value);

/**
 * Regex: disallow any digits in the value.
 */
export const noNumbersAllowed = helpers.regex(/^([^0-9]*)$/);

/**
 * Validates Finnish SSN format.
 * Allows empty values (use `required` separately to enforce presence).
 */
export const ssn = helpers.withMessage(
  'Invalid SSN',
  (value) => !helpers.req(value) || (!value.includes('+') && FinnishSSN.validate(value.toUpperCase()))
);

/**
 * Validates that the value contains only digits (0-9).
 */
export const phone = helpers.withMessage(
  'Invalid phone',
  (value) => /^[0-9]*$/.test(value)
);

/**
 * Custom validator instead of Vuelidate 2's built-in sameAs().
 * sameAs() requires a reactive ref, which is not available in
 * static shared validations files. The (value, siblings) signature
 * is Vuelidate 2's way of accessing sibling fields in custom validators.
 */
export const sameAsEmail = (value, siblings) => value === siblings.Email;

/**
 * "Or" validators: these allow a field to be empty/invalid when a
 * companion field (identified by `prop`) is already filled.
 * Used for address fields that are optional when usePrimaryApplicantAddress is set.
 *
 * In Vuelidate 2, the second argument `vm` is the siblings object,
 * so we access the companion field via `vm[prop]`.
 */
export const requiredOr = (prop) => helpers.withMessage(
  'Required',
  (value, vm) => filled(vm[prop]) || filled(value)
);

export const numericOr = (prop) => helpers.withMessage(
  'Must be numeric',
  (value, vm) => filled(vm[prop]) || /^\d*$/.test(value)
);

export const noDigitsOr = (prop) => helpers.withMessage(
  'No digits allowed',
  (value, vm) => filled(vm[prop]) || !/\d/.test(value)
);

export const maxLengthOr = (max, prop) => {
  const validator = helpers.withMessage(
    ({ $model }) => sprintf(
      translate('Kentän maksimipituus on %s merkkiä. (%s)'),
      max,
      String($model || '').length
    ),
    helpers.withParams({ max }, (value, vm) => {
      return filled(vm[prop]) || String(value || '').length <= max;
    })
  );
  // Ensure $params is set even if helpers.withMessage doesn't propagate it
  validator.$params = { ...validator.$params, max };
  return validator;
};

export const minLengthOr = (min, prop) => {
  const validator = helpers.withMessage(
    ({ $model }) => sprintf(
      translate('Kentän minimipituus on %s merkkiä. (%s)'),
      min,
      String($model || '').length
    ),
    helpers.withParams({ min }, (value, vm) => {
      return filled(vm[prop]) || String(value || '').length >= min;
    })
  );
  // Ensure $params is set even if helpers.withMessage doesn't propagate it
  validator.$params = { ...validator.$params, min };
  return validator;
};

/**
 * Checks that the same SSN does not appear more than once across all applicants.
 * Must be called with component context (`this`) to access `this.allSsns`.
 */
export function applicationUniqueSsn(valueRaw) {
  const normalize = (string) => string.toLowerCase();
  const value = normalize(valueRaw);

  console.log('[applicationUniqueSsn] this:', this);
  console.log('[applicationUniqueSsn] this.allSsns:', this?.allSsns);
  console.log('[applicationUniqueSsn] valueRaw:', valueRaw);

  // Guard against undefined allSsns
  if (!this?.allSsns) {
    console.warn('[applicationUniqueSsn] allSsns is undefined - this context is wrong!');
    return true; // Allow validation to pass if allSsns not available
  }

  // Valid when 0 or 1 occurrences. Only invalid when duplicated (2+).
  const count = this.allSsns.filter((a) => normalize(a) === value).length;
  console.log('[applicationUniqueSsn] duplicate count:', count);
  return count <= 1;
}
